import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Global 401 handling: a session that is truly invalid/expired triggers ONE
// clean logout event (AuthContext listens). Transient 5xx/network errors are
// NEVER treated as logout — this fixes the "randomly logged out" bug.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.startsWith("/auth/")) {
      window.dispatchEvent(new CustomEvent("salesos:unauthorized"));
    }
    return Promise.reject(err);
  }
);

// `opts.zone`: append an explicit " IST" suffix. Off by default (existing
// call sites across the app render this inline next to their own "when"
// label and stay unchanged); pass `{ zone: true }` at a surface where the
// same value could otherwise be mistaken for UTC or the viewer's own
// browser-local time sitting right next to another timestamp (e.g. the
// Billing page's scheduler summary / recent activity / notifications,
// stabilization round 9 — see BillingSubscriptions.jsx and AppShell.jsx).
//
// 2026-09 R13 — the year is now shown AUTOMATICALLY whenever `iso` falls in
// a different IST calendar year than right now, even when the caller never
// asked for it (`opts.year` still forces it on unconditionally; the only
// way to suppress it entirely is the new `opts.year === false`). Root
// cause this fixes: Process/Cases' own due-date cell called this with no
// options at all, so a deadline stored in a PAST year (e.g. from stale/
// legacy test-fixture data) rendered as a bare "03 Oct" — genuinely,
// correctly overdue per the backend's own `d < now` comparison, but
// displayed in a way indistinguishable from an upcoming date in the
// CURRENT year, making a correct overdue flag look like a bug. This is a
// display-only fix — it changes no stored data and no overdue/aggregate
// computation, which were already correct.
export const fmtIST = (iso, opts = {}) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const istYear = (dt) => Number(new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata", year: "numeric",
    }).format(dt));
    const differentYear = istYear(d) !== istYear(new Date());
    const showYear = opts.year === false ? false : (opts.year || differentYear);
    const out = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      ...(showYear ? { year: "numeric" } : {}),
      ...(opts.time !== false ? { hour: "2-digit", minute: "2-digit", hour12: true } : {}),
    }).format(d);
    return opts.zone ? `${out} IST` : out;
  } catch {
    return iso;
  }
};

export const fmtISTTime = (iso) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true,
  }).format(new Date(iso));
};

export const errDetail = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (d.message) return d.next_action ? `${d.message} Next: ${d.next_action}` : d.message;
  return JSON.stringify(d);
};
